import React, { useState, useMemo, useEffect } from 'react';
import { Hospital, AdAsset, AdSettings, DEFAULT_AD_SETTINGS, ScreenSession } from '../types';
import { 
    ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, 
    Clock, Megaphone, Info, Eye, EyeOff, Pencil, Trash2,
    LayoutGrid, List
} from 'lucide-react';
import { AdWizard } from './AdWizard';
import { apiFetch } from '../services/cloudflareService';

interface AdCalendarProps {
    hospital: Hospital;
    onUpdate: (updatedHospital: Hospital) => void;
    screens?: ScreenSession[];
}

export const AdCalendar: React.FC<AdCalendarProps> = ({ hospital, onUpdate, screens = [] }) => {
    const primaryColor = hospital.branding?.primaryColor || '#5B65DC';
    const [settings, setSettings] = useState<AdSettings>(hospital.preferences?.adSettings || DEFAULT_AD_SETTINGS);
    const [assets, setAssets] = useState<AdAsset[]>(hospital.preferences?.adAssets || []);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [showWizard, setShowWizard] = useState(false);
    const [wizardInitialData, setWizardInitialData] = useState<AdAsset | null>(null);
    const [saving, setSaving] = useState(false);

    // Sync state with hospital prop
    useEffect(() => {
        setSettings(hospital.preferences?.adSettings || DEFAULT_AD_SETTINGS);
        setAssets(hospital.preferences?.adAssets || []);
    }, [hospital]);

    const saveAll = async (newSettings: AdSettings, newAssets: AdAsset[]) => {
        setSaving(true);
        try {
            const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8787' : '';
            const newPrefs = { ...hospital.preferences, adSettings: newSettings, adAssets: newAssets };
            const res = await apiFetch(`${baseUrl}/api/hospitals/${hospital.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    preferences: { ...hospital.preferences, adSettings: newSettings, adAssets: newAssets },
                }),
            });
            if (!res.ok) throw new Error('Error guardando anuncios');
            
            setSettings(newSettings);
            setAssets(newAssets);
            onUpdate({ ...hospital, preferences: newPrefs } as Hospital);
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const handleWizardSave = async (asset: AdAsset) => {
        const isEdit = assets.some(a => a.id === asset.id);
        const newAssets = isEdit ? assets.map(a => a.id === asset.id ? asset : a) : [...assets, asset];
        await saveAll(settings, newAssets);
        setShowWizard(false);
        setWizardInitialData(null);
    };

    // Calendar logic
    const monthYear = useMemo(() => {
        return currentDate.toLocaleString('es-MX', { month: 'long', year: 'numeric' });
    }, [currentDate]);

    const daysInMonth = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const date = new Date(year, month, 1);
        const days = [];
        
        // Fill previous month days
        const firstDayIdx = date.getDay() === 0 ? 6 : date.getDay() - 1; // Start with Monday
        for (let i = 0; i < firstDayIdx; i++) {
            const prevDate = new Date(year, month, -i);
            days.unshift({ date: prevDate, isCurrentMonth: false });
        }
        
        // Current month days
        while (date.getMonth() === month) {
            days.push({ date: new Date(date), isCurrentMonth: true });
            date.setDate(date.getDate() + 1);
        }
        
        // Fill next month days to complete grid (42 cells: 6 weeks)
        const remaining = 42 - days.length;
        for (let i = 1; i <= remaining; i++) {
            days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
        }
        
        return days;
    }, [currentDate]);

    const changeMonth = (val: number) => {
        const d = new Date(currentDate);
        d.setMonth(d.getMonth() + val);
        setCurrentDate(d);
    };

    const isToday = (date: Date) => {
        const today = new Date();
        return date.getDate() === today.getDate() && 
               date.getMonth() === today.getMonth() && 
               date.getFullYear() === today.getFullYear();
    };

    const getAdsForDate = (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        return assets.filter(asset => {
            // Ads without schedule are always active — show on all days
            if (!asset.schedule) return true;

            // Check date range
            if (asset.schedule.startDate && dateStr < asset.schedule.startDate) return false;
            if (asset.schedule.endDate && dateStr > asset.schedule.endDate) return false;
            
            // Check day of week
            const dayOfWeek = date.getDay(); // 0 is Sunday, 1 is Monday...
            if (asset.schedule.daysOfWeek && asset.schedule.daysOfWeek.length > 0) {
                if (!asset.schedule.daysOfWeek.includes(dayOfWeek)) return false;
            }
            
            return true;
        });
    };

    const handleDayClick = (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        setWizardInitialData({
            id: '',
            type: 'BANNER_TEXT',
            isActive: true,
            priority: assets.length + 1,
            createdAt: new Date().toISOString(),
            displayTimeSec: 15,
            target: 'ALL',
            schedule: {
                startDate: dateStr,
                endDate: dateStr,
                daysOfWeek: [date.getDay()]
            }
        } as AdAsset);
        setShowWizard(true);
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header / Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="p-3 rounded-2xl bg-indigo-50 border border-indigo-100/50" style={{ backgroundColor: `${primaryColor}10` }}>
                        <CalendarIcon className="w-6 h-6" style={{ color: primaryColor }} />
                    </div>
                    <div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tight capitalize">{monthYear}</h2>
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Planificación Visual</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-xl shadow-sm border border-slate-200/50">
                    <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-white hover:shadow-sm rounded-lg transition-all text-slate-500 hover:text-slate-800">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={() => setCurrentDate(new Date())} className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider hover:bg-white hover:shadow-sm rounded-lg transition-all text-slate-500 hover:text-slate-800">
                        Hoy
                    </button>
                    <button onClick={() => changeMonth(1)} className="p-2 hover:bg-white hover:shadow-sm rounded-lg transition-all text-slate-500 hover:text-slate-800">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>

                <button 
                    onClick={() => { setWizardInitialData(null); setShowWizard(true); }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-bold text-xs shadow-lg transition-all hover:scale-105 active:scale-95 shadow-indigo-500/20"
                    style={{ backgroundColor: primaryColor }}
                >
                    <Plus className="w-4 h-4" />
                    Nueva Campaña
                </button>
            </div>

            {/* Calendar Grid */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden">
                <div className="grid grid-cols-7 border-b border-slate-50">
                    {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
                        <div key={d} className="py-4 text-center text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] bg-slate-50/50">
                            {d}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7">
                    {daysInMonth.map((day, idx) => {
                        const dayAds = getAdsForDate(day.date);
                        const isTodayVal = isToday(day.date);
                        
                        return (
                            <div 
                                key={idx} 
                                onClick={() => handleDayClick(day.date)}
                                className={`min-h-[140px] border-r border-b border-slate-50 p-2 transition-all cursor-pointer hover:bg-slate-50/50 group relative ${!day.isCurrentMonth ? 'bg-slate-50/20 opacity-40' : ''}`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <span className={`text-xs font-black w-7 h-7 flex items-center justify-center rounded-full transition-all ${isTodayVal ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'text-slate-400 group-hover:text-slate-600'}`} style={isTodayVal ? { backgroundColor: primaryColor } : {}}>
                                        {day.date.getDate()}
                                    </span>
                                    {dayAds.length > 0 && day.isCurrentMonth && (
                                        <span className="text-[9px] font-bold text-slate-300">
                                            {dayAds.length} {dayAds.length === 1 ? 'anuncio' : 'anuncios'}
                                        </span>
                                    )}
                                </div>
                                <div className="space-y-1 relative z-10">
                                    {dayAds.slice(0, 3).map(ad => (
                                        <div 
                                            key={ad.id} 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setWizardInitialData(ad);
                                                setShowWizard(true);
                                            }}
                                            className="px-2 py-1 rounded-md text-[9px] font-bold truncate border shadow-sm flex items-center gap-1 transition-all hover:scale-[1.05] hover:shadow-md" 
                                            style={{ 
                                                backgroundColor: ad.isActive ? `${primaryColor}10` : '#f1f5f9', 
                                                borderColor: ad.isActive ? `${primaryColor}20` : '#e2e8f0',
                                                color: ad.isActive ? primaryColor : '#94a3b8'
                                            }}
                                        >
                                            <div className="w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: ad.isActive ? primaryColor : '#cbd5e1' }} />
                                            {ad.name || ad.text || ad.imageAlt || 'Sin título'}
                                        </div>
                                    ))}
                                    {dayAds.length > 3 && (
                                        <div className="text-[9px] font-bold text-slate-400 pl-2">
                                            + {dayAds.length - 3} más...
                                        </div>
                                    )}
                                </div>


                                <button 
                                    className="absolute bottom-2 right-2 p-2 rounded-xl shadow-lg border-0 opacity-0 group-hover:opacity-100 transition-all translate-y-2 group-hover:translate-y-0 hover:scale-110 active:scale-95 text-white"
                                    style={{ 
                                        backgroundColor: primaryColor,
                                        boxShadow: `0 4px 12px ${primaryColor}40`
                                    }}
                                >
                                    <Plus className="w-4 h-4" strokeWidth={2.5} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Legend / Info */}
            <div className="flex flex-wrap gap-6 items-center p-6 bg-slate-50 rounded-2xl border border-slate-100">
                <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-slate-400" />
                    <p className="text-xs text-slate-500 font-medium">Haz click en cualquier día para programar una campaña o ver detalles.</p>
                </div>
                <div className="flex items-center gap-4 ml-auto">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: primaryColor }} />
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Activos</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-slate-300" />
                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Inactivos</span>
                    </div>
                </div>
            </div>

            {/* Wizard Modal */}
            {showWizard && (
                <AdWizard
                    hospital={hospital}
                    settings={settings}
                    initialData={wizardInitialData}
                    screens={screens}
                    onClose={() => { setShowWizard(false); setWizardInitialData(null); }}
                    onSave={handleWizardSave}
                    onDelete={async (id) => {
                        const asset = assets.find(a => a.id === id);
                        if (asset?.imageUrl && asset.imageUrl.startsWith('/api/ad-media/')) {
                            try {
                                const key = asset.imageUrl.replace('/api/ad-media/', '');
                                const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8787' : '';
                                await apiFetch(`${baseUrl}/api/ad-media`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ key }) });
                            } catch (e) { console.error(e); }
                        }
                        await saveAll(settings, assets.filter(a => a.id !== id));
                    }}
                />
            )}
        </div>
    );
};
